import { CampaignBlocks, getDefaultBlockStructure } from "@kvix/shared";
import { Container, FormControl, FormLabel, Grid, makeStyles, useMediaQuery, useTheme } from "@material-ui/core";
import { motion, useTransform, useViewportScroll } from "framer-motion";
import React, { useContext, useMemo, useRef } from "react";
import { campaignBlockList } from "..";
import { Button } from "../../Inputs";
import { Text } from "../../Primitives";
import { AdminFormInput } from "../admin";
import { AdminCampaignContentContext } from "../context/adminCampaignContentContext";
import { useRefScrollProgress } from "../utils/getRefScrollProgress";
import { useWindowSize } from "../utils/getWindowSize";
import { FeatureBlock } from "./FeatureBlock";
const useStyles = makeStyles((theme) => ({
    childBlock: {
        paddingLeft: "7px",
        paddingRight: "8px",
        borderLeft: `1px solid ${theme.palette.primary.main}`
    },
    childTitle: {
        fontWeight: 600
    },
    centerBlock: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        color: theme.palette.common.white,
        backgroundColor: theme.palette.primary.dark,
        padding: "80px 0  100px 0"
    },
    firstTitle: { paddingBottom: "20px", lineHeight: "48px", fontSize: "46px" },
    ingress: { lineHeight: "24px", fontSize: "16px", fontWeight: 500 },
    signUpToday: { lineHeight: "24px", fontSize: "16px", fontWeight: 500 },
    button: {
        marginTop: "20px ",
        height: "44px",
        width: "320px",
        borderRadius: "30px",
        textTransform: "capitalize",
        "&:active": {
            animation: "$scaleButton linear 300ms "
        },
        "&:hover": {
            background: theme.palette.primary.main
        },
        [theme.breakpoints.up("sm")]: {
            margin: "20px 0 40px 0"
        }
    },
    "@keyframes scaleButton": {
        "0%": {
            transform: "scale(1)"
        },
        "50%": {
            transform: "scale(1.1)"
        },
        "100%": {
            transform: "scale(1)"
        }
    },
    gridContainer: {
        margin: "60px 0",
        textAlign: "left",
        display: "flex",
        justifyContent: "center",
        maxWidth: "1000px",
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column"
        }
    },
    gridItem: {
        display: "flex",
        padding: "0 10px 60px 10px",
        maxWidth: "100%",
        "&:last-child": {
            padding: "0 10px 0 10px"
        }
    },
    motionGridItem: {
        display: "flex",
        [theme.breakpoints.up("sm")]: {
            padding: "30px"
        }
    }
}));
export class CtaWithFeaturesBlock {
    constructor() {
        this.childBlockStructure = getDefaultBlockStructure(FeatureBlock);
        this.defaultStructure = {
            name: CampaignBlocks.CtaWithFeaturesBlock,
            titles: [""],
            texts: ["", ""],
            buttons: [{ text: "" }],
            subBlocks: [
                this.childBlockStructure,
                this.childBlockStructure,
                this.childBlockStructure,
                this.childBlockStructure
            ]
        };
        this.Render = (props) => {
            const classes = useStyles();
            const theme = useTheme();
            const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
            const { scrollY } = useViewportScroll();
            const size = useWindowSize();
            const featureBlockRef = useRef(null);
            const { ref, start, end } = useRefScrollProgress(featureBlockRef);
            const actualStart = start - size.height;
            const actualEnd = end - size.height;
            const opacityValues = {
                headerOpacity: useTransform(scrollY, [actualStart - 100, actualEnd - 300], [0, 1]),
                opacity0: useTransform(scrollY, [actualStart + 100, actualEnd - 300], [0, 1]),
                opacity1: useTransform(scrollY, [actualStart + 200, actualEnd - 200], [0, 1]),
                opacity2: useTransform(scrollY, [actualStart + 300, actualEnd], [0, 1]),
                opacity3: useTransform(scrollY, [actualStart + 400, actualEnd + 100], [0, 1])
            };
            const opacityValuesMobile = {
                headerOpacity: useTransform(scrollY, [actualStart - 100, actualEnd - 500], [0, 1]),
                opacity0: useTransform(scrollY, [actualStart + 100, actualEnd - 400], [0, 1]),
                opacity1: useTransform(scrollY, [actualStart + 300, actualEnd - 200], [0, 1]),
                opacity2: useTransform(scrollY, [actualStart + 500, actualEnd], [0, 1]),
                opacity3: useTransform(scrollY, [actualStart + 700, actualEnd + 200], [0, 1])
            };
            const { block, campaignContext } = props;
            const adminContext = useContext(AdminCampaignContentContext);
            const onClick = () => {
                campaignContext === null || campaignContext === void 0 ? void 0 : campaignContext.openModal(campaignContext);
            };
            return (React.createElement("div", { className: classes.centerBlock },
                React.createElement(Container, { maxWidth: "lg" },
                    React.createElement(motion.div, { style: adminContext === null && isMobile
                            ? { ["opacity"]: opacityValuesMobile.headerOpacity }
                            : { ["opacity"]: opacityValues.headerOpacity } },
                        React.createElement(Text, { className: classes.firstTitle, variant: "h1" }, block.titles[0]),
                        React.createElement(Text, { className: classes.ingress, variant: "h4" }, block.texts[0]))),
                React.createElement(Grid, { ref: featureBlockRef, container: true, className: classes.gridContainer }, block.subBlocks.map((b, index) => {
                    const featureBlock = new FeatureBlock();
                    return (React.createElement(Grid, { key: index, item: true, sm: 6, className: classes.gridItem },
                        React.createElement(motion.div, { className: classes.motionGridItem, style: adminContext === null
                                ? isMobile
                                    ? {
                                        ["opacity"]: opacityValuesMobile[`opacity${index}`]
                                    }
                                    : {
                                        ["opacity"]: opacityValues[`opacity${index}`]
                                    }
                                : null },
                            React.createElement(featureBlock.Render, { block: b }))));
                })),
                React.createElement(Container, { maxWidth: "sm" },
                    React.createElement(Text, { variant: "h5" }, block.texts[1]),
                    React.createElement(Button, { className: classes.button, onClick: onClick }, block.buttons[0].text))));
        };
        this.Compose = (props) => {
            const classes = useStyles();
            const { block } = props;
            const { updateContent } = useContext(AdminCampaignContentContext);
            const titleOnChange = (event) => {
                block.titles[0] = event.target.value;
                updateContent(block);
            };
            const text1OnChange = (event) => {
                block.texts[0] = event.target.value;
                updateContent(block);
            };
            const text2OnChange = (event) => {
                block.texts[1] = event.target.value;
                updateContent(block);
            };
            const buttonTextOnChange = (event) => {
                block.buttons[0].text = event.target.value;
                updateContent(block);
            };
            const childComposeAction = (b, index) => {
                block.subBlocks[index] = b;
                updateContent(block);
            };
            const children = useMemo(() => {
                return block.subBlocks.map((b, index) => {
                    const blockType = campaignBlockList[b.name];
                    const blockObject = new blockType();
                    return (React.createElement(FormControl, { component: "fieldset", variant: "standard", fullWidth: true, margin: "normal", key: b.name + index, className: classes.childBlock },
                        React.createElement(FormLabel, { component: "legend", className: classes.childTitle }, `${b.name} ${index + 1}`),
                        React.createElement(blockObject.Compose, { composeAction: childComposeAction, block: b, index: index })));
                });
            }, [block.subBlocks]);
            return (React.createElement(React.Fragment, null,
                React.createElement(AdminFormInput, { name: "title", label: "Headline", onChange: titleOnChange, required: true, value: block.titles[0] }),
                React.createElement(AdminFormInput, { name: "text1", label: "Subheader", onChange: text1OnChange, required: true, value: block.texts[0] }),
                React.createElement(AdminFormInput, { name: "text2", label: "Call To Action", onChange: text2OnChange, required: true, value: block.texts[1] }),
                React.createElement(AdminFormInput, { name: "buttonText", label: "Button Text", onChange: buttonTextOnChange, required: true, value: block.buttons[0].text }),
                children));
        };
    }
}
//# sourceMappingURL=CtaWithFeaturesBlock.js.map